import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import { Icd10Service } from '../../services/reference/icd10.service';
import type { Icd10Code } from '../../dtos/reference/icd10.dto';

export interface Icd10PickerProps {
  id: string;
  value: Icd10Code | null;
  onChange: (code: Icd10Code | null) => void;
  /** Visible label; omit when the form renders its own <label htmlFor={id}> */
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Red ring, e.g. a required field left empty */
  invalid?: boolean;
  /** Codes already chosen elsewhere (shown, but not selectable) */
  excludeCodes?: string[];
  className?: string;
}

const MIN_CHARS = 2;

/**
 * ICD-10-CM type-ahead over the full CMS code set (server search, PRD REQ-PAT-05 / BIL-07).
 * Only billable codes can be picked; a typed category code (G40) lists its billable codes and
 * shows the category itself as "choose a more specific code".
 */
const Icd10Picker: React.FC<Icd10PickerProps> = ({
  id, value, onChange, label, placeholder = 'Search ICD-10 by code or description', disabled = false, invalid = false, excludeCodes = [], className = ''
}) => {
  const [editing, setEditing] = useState(!value);
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query.trim(), 300);
  const [results, setResults] = useState<Icd10Code[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = `${id}-list`;

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!value) setEditing(true);
  }, [value]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        // Leaving a change of an existing value keeps that value
        if (valueRef.current) { setEditing(false); setQuery(''); }
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (disabled || debounced.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setError(null);
    Icd10Service.search(debounced, { limit: 20 })
      .then((r) => {
        if (!live) return;
        setResults(r.codes);
        setActive(r.codes.findIndex((c) => c.billable && !excludeCodes.includes(c.code)));
      })
      .catch(() => { if (live) { setResults([]); setError('ICD-10 search is unavailable right now.'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
    // excludeCodes is read when results arrive; re-searching on its identity would loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, disabled]);

  const selectable = (c: Icd10Code) => c.billable && !excludeCodes.includes(c.code);

  const pick = (c: Icd10Code) => {
    if (!selectable(c)) return;
    onChange(c);
    setQuery('');
    setResults([]);
    setOpen(false);
    setEditing(false);
  };

  const move = (step: number) => {
    if (!results.length) return;
    let next = active;
    for (let i = 0; i < results.length; i++) {
      next = (next + step + results.length) % results.length;
      if (selectable(results[next])) { setActive(next); return; }
    }
  };

  const optionId = (i: number) => `${id}-opt-${i}`;
  const showList = open && query.trim().length >= MIN_CHARS;

  if (value && !editing) {
    return (
      <div className={`min-w-0 ${className}`}>
        {label && <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>}
        <div className={`flex min-h-[44px] items-center justify-between gap-2 rounded-md border bg-white px-3 ${invalid ? 'border-red-300' : 'border-gray-300'}`}>
          <button
            type="button"
            id={id}
            disabled={disabled}
            className="min-w-0 flex-1 truncate text-left text-base md:text-sm disabled:cursor-default"
            onClick={() => { setEditing(true); setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
            title={`${value.code} — ${value.description}`}
          >
            <span className="font-mono font-medium">{value.code}</span>
            {value.description && <span className="text-gray-600"> — {value.description}</span>}
          </button>
          {!disabled && (
            <button
              type="button"
              aria-label={`Clear ICD-10 code ${value.code}`}
              className="flex h-9 w-9 shrink-0 items-center justify-center text-gray-400 hover:text-gray-700"
              onClick={() => { onChange(null); setEditing(true); }}
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={`relative min-w-0 ${className}`}>
      {label && <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">{label}</label>}
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={showList && active >= 0 ? optionId(active) : undefined}
          aria-invalid={invalid || undefined}
          autoComplete="off"
          disabled={disabled}
          value={query}
          placeholder={placeholder}
          className={`min-h-[44px] w-full rounded-md border pl-9 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 md:text-sm ${invalid ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); move(1); }
            if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
            if (e.key === 'Enter' && showList && results[active]) { e.preventDefault(); pick(results[active]); }
            if (e.key === 'Escape') {
              e.stopPropagation();
              setOpen(false);
              if (value) { setEditing(false); setQuery(''); }
            }
          }}
        />
      </div>
      {open && query.trim().length > 0 && query.trim().length < MIN_CHARS && (
        <p className="mt-1 text-xs text-gray-500">Type at least {MIN_CHARS} characters.</p>
      )}
      {error && <p role="alert" className="mt-1 text-xs text-red-700">{error}</p>}
      {showList && (
        <ul id={listId} role="listbox" aria-label="ICD-10 codes" className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {loading && <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>}
          {!loading && !error && results.length === 0 && debounced.length >= MIN_CHARS && (
            <li className="px-3 py-2 text-sm text-gray-500">No ICD-10 codes match. Try a code (G40.909) or other words.</li>
          )}
          {!loading && results.map((c, i) => {
            const ok = selectable(c);
            const note = !c.billable ? 'Category — choose a more specific code' : excludeCodes.includes(c.code) ? 'Already added' : null;
            return (
              <li
                key={c.code}
                id={optionId(i)}
                role="option"
                aria-selected={i === active}
                aria-disabled={!ok || undefined}
                className={`flex min-h-[44px] items-center justify-between gap-2 px-3 text-sm ${ok ? 'cursor-pointer' : 'cursor-default text-gray-400'} ${i === active ? 'bg-slate-100' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                onMouseEnter={() => ok && setActive(i)}
              >
                <span className="min-w-0">
                  <span className="font-mono font-medium">{c.code}</span> <span className={ok ? 'text-gray-700' : ''}>— {c.description}</span>
                </span>
                {note && <span className="shrink-0 text-xs font-medium text-amber-700">{note}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Icd10Picker;
